import { useEffect, useState } from "react";
import { Loader2, Plus, ShieldCheck, UserCog } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/sonner";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { useApi } from "@/hooks/use-api";
import { api, errorMessage } from "@/lib/api";
import { PageHeader, SectionTitle } from "@/components/common/PageHeader";
import { EmptyState } from "@/components/common/EmptyState";
import { ROLE_LABEL } from "@/lib/status";
import { fmtDateTime, fmtRelative } from "@/lib/format";

const ProfileTab = () => {
  const { user, refresh } = useAuth();
  const { isDark, toggle } = useTheme();
  const [form, setForm] = useState({ name: user?.name || "", phone: user?.phone || "", title: user?.title || "" });
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.patch("/auth/me", form);
      await refresh();
      toast.success("Profilen er oppdatert");
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <form onSubmit={save} className="surface surface-pad space-y-4" data-testid="profile-form">
        <SectionTitle title="Min profil" description={user?.email} />
        <div className="space-y-1.5">
          <Label htmlFor="p-name">Navn</Label>
          <Input id="p-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="profile-name" />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="p-title">Stilling</Label>
            <Input id="p-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="profile-title" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="p-phone">Telefon</Label>
            <Input id="p-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="profile-phone" />
          </div>
        </div>
        <div className="flex items-center justify-between rounded-xl border border-border/80 bg-muted/40 px-3.5 py-2.5 text-sm">
          <span className="text-muted-foreground">Rolle</span>
          <Badge variant="soft" className="font-medium" data-testid="profile-role">
            {ROLE_LABEL[user?.role]}
          </Badge>
        </div>
        <Button type="submit" disabled={busy} data-testid="profile-save">
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> : null} Lagre
        </Button>
      </form>
      <div className="surface surface-pad space-y-4">
        <SectionTitle title="Visning" />
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-medium">Mørkt tema</div>
            <div className="text-xs text-muted-foreground">Lys stil er standard. Valget lagres i nettleseren.</div>
          </div>
          <Switch checked={isDark} onCheckedChange={toggle} data-testid="settings-theme-switch" />
        </div>
        <div className="rounded-xl border border-border/80 bg-muted/40 p-3.5 text-xs text-muted-foreground">
          <div className="flex items-center gap-2 font-medium text-foreground">
            <ShieldCheck className="h-3.5 w-3.5 text-primary" aria-hidden="true" /> Sikkerhet og tilgang
          </div>
          <p className="mt-1">All datatilgang er avgrenset til din organisasjon i backend. Innlogginger, eksporter, kvitteringer og assistentspørsmål logges i aktivitetsloggen.</p>
        </div>
      </div>
    </div>
  );
};

const NotificationsTab = () => {
  const { data, isLoading, refetch } = useApi("/settings/notifications");
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (data && !form) setForm(data);
  }, [data, form]);
  const save = async () => {
    setBusy(true);
    try {
      const { user_id, channels_note, updated_at, ...body } = form;
      await api.put("/settings/notifications", body);
      toast.success("Varslingsinnstillinger lagret");
      refetch();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  if (isLoading || !form) return <Skeleton className="h-64 w-full" />;
  const sevToggle = (s) => setForm({ ...form, severities: form.severities.includes(s) ? form.severities.filter((x) => x !== s) : [...form.severities, s] });
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <div className="surface surface-pad space-y-5" data-testid="notification-settings">
        <SectionTitle title="Varslingskanaler" description="Klargjort – aktiveres når portalen kobles til reelle driftsdata" />
        {[
          ["email_enabled", "E-post", "Varsler sendes til din registrerte e-postadresse"],
          ["sms_enabled", "SMS", "Krever registrert mobilnummer"],
          ["push_enabled", "Push i mobilapp", "Tilgjengelig når portalen installeres som app"],
        ].map(([k, l, d]) => (
          <div key={k} className="flex items-center justify-between gap-4">
            <div>
              <div className="text-sm font-medium">{l}</div>
              <div className="text-xs text-muted-foreground">{d}</div>
            </div>
            <Switch checked={!!form[k]} onCheckedChange={(v) => setForm({ ...form, [k]: v })} data-testid={`notif-${k}`} />
          </div>
        ))}
        <div className="rounded-xl border border-dashed border-border p-3.5 text-xs leading-relaxed text-muted-foreground">{data.channels_note}</div>
      </div>
      <div className="surface surface-pad space-y-5">
        <SectionTitle title="Hva vil du varsles om?" />
        <div className="space-y-3">
          {[
            ["critical", "Kritiske hendelser (utfall)"],
            ["warning", "Advarsler (redusert drift, nettutfall, alarmer)"],
            ["info", "Informasjon (vedlikehold, kommunikasjon)"],
          ].map(([s, l]) => (
            <label key={s} className="flex items-center gap-3 text-sm cursor-pointer">
              <Checkbox checked={form.severities.includes(s)} onCheckedChange={() => sevToggle(s)} data-testid={`notif-sev-${s}`} /> {l}
            </label>
          ))}
        </div>
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="text-sm font-medium">Kun produksjonsstans</div>
            <div className="text-xs text-muted-foreground">Ignorer alarmer som ikke stanser produksjonen</div>
          </div>
          <Switch checked={form.production_stop_only} onCheckedChange={(v) => setForm({ ...form, production_stop_only: v })} data-testid="notif-stop-only" />
        </div>
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="text-sm font-medium">Daglig oppsummering</div>
            <div className="text-xs text-muted-foreground">Produksjon og hendelser siste døgn, kl. 07:00</div>
          </div>
          <Switch checked={form.daily_summary} onCheckedChange={(v) => setForm({ ...form, daily_summary: v })} data-testid="notif-daily" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>Stille periode fra</Label>
            <Input type="time" value={form.quiet_hours_start || ""} onChange={(e) => setForm({ ...form, quiet_hours_start: e.target.value })} data-testid="notif-quiet-start" />
          </div>
          <div className="space-y-1.5">
            <Label>til</Label>
            <Input type="time" value={form.quiet_hours_end || ""} onChange={(e) => setForm({ ...form, quiet_hours_end: e.target.value })} data-testid="notif-quiet-end" />
          </div>
        </div>
        <Button onClick={save} disabled={busy} data-testid="notif-save">
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> : null} Lagre innstillinger
        </Button>
      </div>
    </div>
  );
};

const UserDialog = ({ open, onOpenChange, plants, existing, onSaved }) => {
  const [form, setForm] = useState(existing ? { ...existing, password: "", plant_ids: existing.plant_ids || [] } : { email: "", name: "", role: "customer_user", plant_ids: [], phone: "", title: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [allPlants, setAllPlants] = useState(!existing || !existing.plant_ids);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { ...form, plant_ids: form.role === "customer_admin" || allPlants ? null : form.plant_ids };
      if (existing) {
        if (!body.password) delete body.password;
        delete body.email;
        await api.patch(`/org/users/${existing.id}`, body);
        toast.success("Brukeren er oppdatert");
      } else {
        await api.post("/org/users", body);
        toast.success("Brukeren er opprettet");
      }
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="user-dialog">
        <DialogHeader>
          <DialogTitle>{existing ? "Rediger bruker" : "Ny bruker"}</DialogTitle>
          <DialogDescription>Brukeren får kun tilgang til kraftverk i din organisasjon.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5 sm:col-span-2">
              <Label>E-post</Label>
              <Input type="email" value={form.email} disabled={!!existing} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email" />
            </div>
            <div className="space-y-1.5">
              <Label>Navn</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name" />
            </div>
            <div className="space-y-1.5">
              <Label>Stilling</Label>
              <Input value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="user-title" />
            </div>
            <div className="space-y-1.5">
              <Label>Rolle</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="customer_user">Bruker (lesetilgang)</SelectItem>
                  <SelectItem value="customer_admin">Kunde-administrator</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{existing ? "Nytt passord (valgfritt)" : "Passord"}</Label>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Minst 8 tegn" data-testid="user-password" />
            </div>
          </div>
          {form.role === "customer_user" ? (
            <div className="space-y-2 rounded-xl border border-border/80 p-3.5">
              <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
                <Checkbox checked={allPlants} onCheckedChange={(v) => setAllPlants(!!v)} data-testid="user-all-plants" /> Tilgang til alle kraftverk
              </label>
              {!allPlants ? (
                <div className="grid max-h-48 gap-1.5 overflow-y-auto scroll-thin sm:grid-cols-2" data-testid="user-plant-list">
                  {plants.map((p) => (
                    <label key={p.id} className="flex items-center gap-2 text-xs cursor-pointer">
                      <Checkbox checked={form.plant_ids.includes(p.id)} onCheckedChange={(v) => setForm({ ...form, plant_ids: v ? [...form.plant_ids, p.id] : form.plant_ids.filter((x) => x !== p.id) })} /> {p.name}
                    </label>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Avbryt
            </Button>
            <Button type="submit" disabled={busy} data-testid="user-save">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> : null} Lagre
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const UsersTab = () => {
  const { data, isLoading, refetch, isError } = useApi("/org/users");
  const [dialog, setDialog] = useState(null); // null | 'new' | user
  const toggleActive = async (u) => {
    try {
      await api.patch(`/org/users/${u.id}`, { active: !u.active });
      toast.success(u.active ? "Brukeren er deaktivert" : "Brukeren er aktivert");
      refetch();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };
  if (isError) return <EmptyState icon={UserCog} title="Velg en kunde for å administrere brukere" description="Proxima-administratorer må velge aktiv kunde i menyen øverst." />;
  if (isLoading) return <Skeleton className="h-64 w-full" />;
  const plantName = (id) => data.plants.find((p) => p.id === id)?.name?.replace(" kraftverk", "");
  return (
    <section className="surface surface-pad" data-testid="users-section">
      <SectionTitle
        title="Brukere og tilganger"
        description="Kunde-administratorer ser alle kraftverk. Brukere kan avgrenses til utvalgte kraftverk."
        actions={
          <Button size="sm" className="gap-1.5" onClick={() => setDialog("new")} data-testid="users-new-button">
            <Plus className="h-4 w-4" aria-hidden="true" /> Ny bruker
          </Button>
        }
      />
      <div className="mt-3 overflow-x-auto scroll-thin">
        <Table data-testid="users-table">
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Bruker</TableHead>
              <TableHead>Rolle</TableHead>
              <TableHead>Tilgang</TableHead>
              <TableHead>Sist innlogget</TableHead>
              <TableHead className="text-right">Handling</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.users.map((u) => (
              <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                <TableCell>
                  <div className="font-medium">{u.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {u.email}
                    {u.title ? ` · ${u.title}` : ""}
                  </div>
                </TableCell>
                <TableCell className="text-sm">{ROLE_LABEL[u.role]}</TableCell>
                <TableCell className="text-xs text-muted-foreground max-w-xs">{u.plant_ids ? u.plant_ids.map(plantName).join(", ") : "Alle kraftverk"}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{u.last_login_at ? fmtRelative(u.last_login_at) : "Aldri"}</TableCell>
                <TableCell className="text-right">
                  <div className="inline-flex items-center gap-2">
                    {!u.active ? <Badge variant="outline">Deaktivert</Badge> : null}
                    <Button size="sm" variant="ghost" onClick={() => setDialog(u)} data-testid={`user-edit-${u.id}`}>
                      Rediger
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => toggleActive(u)} data-testid={`user-toggle-${u.id}`}>
                      {u.active ? "Deaktiver" : "Aktiver"}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {dialog ? <UserDialog key={dialog === "new" ? "new" : dialog.id} open onOpenChange={(o) => !o && setDialog(null)} plants={data.plants} existing={dialog === "new" ? null : dialog} onSaved={refetch} /> : null}
    </section>
  );
};

const ACTION_LABEL = {
  login: "Innlogging",
  login_failed: "Mislykket innlogging",
  logout: "Utlogging",
  timeseries_read: "Lest tidsserie",
  export: "Eksport",
  event_acknowledged: "Hendelse kvittert",
  event_resolved: "Hendelse lukket",
  service_request_created: "Henvendelse opprettet",
  service_request_updated: "Henvendelse oppdatert",
  assistant_query: "Assistent-spørsmål",
  user_created: "Bruker opprettet",
  user_updated: "Bruker oppdatert",
  notification_settings_updated: "Varsling endret",
};

const AuditTab = () => {
  const { data, isLoading, isError } = useApi("/audit", { limit: 150 }, { staleTime: 10_000 });
  if (isError) return <EmptyState title="Ingen tilgang til aktivitetsloggen" />;
  if (isLoading) return <Skeleton className="h-64 w-full" />;
  return (
    <section className="surface surface-pad" data-testid="audit-section">
      <SectionTitle title="Aktivitetslogg" description="Sporbarhet for innlogginger, datatilgang, eksporter og handlinger" />
      <div className="mt-3 overflow-x-auto scroll-thin">
        <Table data-testid="audit-table">
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Tidspunkt</TableHead>
              <TableHead>Handling</TableHead>
              <TableHead>Bruker</TableHead>
              <TableHead>Detaljer</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.entries.map((e) => (
              <TableRow key={e.id}>
                <TableCell className="mono whitespace-nowrap text-xs">{fmtDateTime(e.ts)}</TableCell>
                <TableCell className="text-sm font-medium">{ACTION_LABEL[e.action] || e.action}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{e.user_email || "–"}</TableCell>
                <TableCell className="text-xs text-muted-foreground max-w-md truncate">{e.details?.question || e.details?.title || e.details?.subject || e.details?.email || (e.details?.format ? `${e.details.format.toUpperCase()} · ${e.details.rows} rader` : "") || e.resource || ""}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {!data.entries.length ? <EmptyState title="Ingen aktivitet logget ennå" className="mt-3" /> : null}
    </section>
  );
};

export default function SettingsPage() {
  const { isAdmin } = useAuth();
  return (
    <div className="space-y-7" data-testid="settings-page">
      <PageHeader eyebrow="Konto" title="Innstillinger" description="Profil, varsling og – for administratorer – brukere og aktivitetslogg." />
      <Tabs defaultValue="profile">
        <TabsList className="tabs-line flex-wrap">
          <TabsTrigger value="profile" data-testid="settings-tab-profile">
            Profil
          </TabsTrigger>
          <TabsTrigger value="notifications" data-testid="settings-tab-notifications">
            Varsling
          </TabsTrigger>
          {isAdmin ? (
            <TabsTrigger value="users" data-testid="settings-tab-users">
              Brukere
            </TabsTrigger>
          ) : null}
          {isAdmin ? (
            <TabsTrigger value="audit" data-testid="settings-tab-audit">
              Aktivitetslogg
            </TabsTrigger>
          ) : null}
        </TabsList>
        <TabsContent value="profile" className="mt-5">
          <ProfileTab />
        </TabsContent>
        <TabsContent value="notifications" className="mt-5">
          <NotificationsTab />
        </TabsContent>
        {isAdmin ? (
          <TabsContent value="users" className="mt-5">
            <UsersTab />
          </TabsContent>
        ) : null}
        {isAdmin ? (
          <TabsContent value="audit" className="mt-5">
            <AuditTab />
          </TabsContent>
        ) : null}
      </Tabs>
    </div>
  );
}
